<template>
  <div v-if="measures && measures.length">
    <div class="flex gap-3 selection:flex-wrap">
      <div
        v-for="(measure, key) in measures"
        :key="key"
        class="basis-1/2 md:basis-1/4"
      >
        <FimPicture
          provider="local"
          :src="`/img/sunglasses-measures/${measure.file}`"
          :alt="measure.alt"
          layout="raw"
          class="block h-auto w-full"
          loading="lazy"
          decoding="auto"
        />

        <div
          class="measure text-center text-sm leading-[18px]"
          :class="'measure-' + key"
        >
          <div class="font-medium">
            {{ measure.size }}
          </div>
          <div>{{ measure.label }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  getAttributeValue,
  getFirstAttributeValue,
  type Product,
  type Variant,
} from '@scayle/storefront-nuxt'

const measureMapping: Record<string, object> = {
  /* @todo: add missing mapping for Monoscheibe */
  achteckig: {
    glassesWidth: 'MehreckigFrameWidth.svg',
    lensWidth: 'MehreckigGlassWidth.svg',
    bridgeWidth: 'MehreckigBridge.svg',
    templeLength: 'MehreckigDepth.svg',
  },
  browline: {
    glassesWidth: 'BrowlineFrameWidth.svg',
    lensWidth: 'BrowlineGlassWidth.svg',
    bridgeWidth: 'BrowlineBridge.svg',
    templeLength: 'BrowlineDepth.svg',
  },
  butterfly: {
    glassesWidth: 'ButterflyFrameWidth.svg',
    lensWidth: 'ButterflyGlassWidth.svg',
    bridgeWidth: 'ButterflyBridge.svg',
    templeLength: 'ButterflyDepth.svg',
  },
  cateye: {
    glassesWidth: 'ButterflyFrameWidth.svg',
    lensWidth: 'ButterflyGlassWidth.svg',
    bridgeWidth: 'ButterflyBridge.svg',
    templeLength: 'ButterflyDepth.svg',
  },
  karee: {
    glassesWidth: 'EckigFrameWidth.svg',
    lensWidth: 'EckigGlassWidth.svg',
    bridgeWidth: 'EckigBridge.svg',
    templeLength: 'EckigDepth.svg',
  },
  eckig: {
    glassesWidth: 'EckigFrameWidth.svg',
    lensWidth: 'EckigGlassWidth.svg',
    bridgeWidth: 'EckigBridge.svg',
    templeLength: 'EckigDepth.svg',
  },
  mehreckig: {
    glassesWidth: 'MehreckigFrameWidth.svg',
    lensWidth: 'MehreckigGlassWidth.svg',
    bridgeWidth: 'MehreckigBridge.svg',
    templeLength: 'MehreckigDepth.svg',
  },
  oval: {
    glassesWidth: 'OvalFrameWidth.svg',
    lensWidth: 'OvalGlassWidth.svg',
    bridgeWidth: 'OvalBridge.svg',
    templeLength: 'OvalDepth.svg',
  },
  panto: {
    glassesWidth: 'PantoFrameWidth.svg',
    lensWidth: 'PantoGlassWidth.svg',
    bridgeWidth: 'PantoBridge.svg',
    templeLength: 'PantoDepth.svg',
  },
  pilot: {
    glassesWidth: 'PilotFrameWidth.svg',
    lensWidth: 'PilotGlassWidth.svg',
    bridgeWidth: 'PilotBridge.svg',
    templeLength: 'PilotDepth.svg',
  },
  quadratisch: {
    glassesWidth: 'EckigFrameWidth.svg',
    lensWidth: 'EckigGlassWidth.svg',
    bridgeWidth: 'EckigBridge.svg',
    templeLength: 'EckigDepth.svg',
  },
  rund: {
    glassesWidth: 'RundFrameWidth.svg',
    lensWidth: 'RundGlassWidth.svg',
    bridgeWidth: 'RundBridge.svg',
    templeLength: 'RundDepth.svg',
  },
  halb: {
    glassesWidth: 'HalbFrameWidth.svg',
    lensWidth: 'HalbGlassWidth.svg',
    bridgeWidth: 'HalbBridge.svg',
    templeLength: 'HalbDepth.svg',
  },
  rechteckig: {
    glassesWidth: 'EckigFrameWidth.svg',
    lensWidth: 'EckigGlassWidth.svg',
    bridgeWidth: 'EckigBridge.svg',
    templeLength: 'EckigDepth.svg',
  },
  sonstige: {
    glassesWidth: 'SonstigeFrameWidth.svg',
    lensWidth: 'SonstigeGlassWidth.svg',
    bridgeWidth: 'SonstigeBridge.svg',
    templeLength: 'SonstigeDepth.svg',
  },
  raute: {
    glassesWidth: 'RauteFrameWidth.svg',
    lensWidth: 'RauteGlassWidth.svg',
    bridgeWidth: 'RauteBridge.svg',
    templeLength: 'RauteDepth.svg',
  },
  schmal: {
    glassesWidth: 'SchmalFrameWidth.svg',
    lensWidth: 'SchmalGlassWidth.svg',
    bridgeWidth: 'SchmalBridge.svg',
    templeLength: 'SchmalDepth.svg',
  },
}

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  currentVariant: {
    type: Object as PropType<Variant>,
    required: true,
  },
})

const shape = computed(() =>
  getAttributeValue(props.product?.attributes, 'shape')?.toLowerCase(),
)

const currentMapping = computed(() => {
  if (shape.value) {
    return measureMapping[shape.value] as Record<string, object>
  }
  return undefined
})

const measures = computed(() => {
  if (!currentMapping.value) {
    return
  }

  return Object.entries(currentMapping.value).map(([key, file]) => {
    const size =
      getFirstAttributeValue(props.currentVariant?.attributes, key)?.label ??
      '0'

    const label =
      props.currentVariant?.attributes &&
      props.currentVariant.attributes[key]?.label

    const alt = `${shape.value} ${label}`

    return {
      file,
      label,
      size: size.endsWith('mm') ? size : size + ' mm',
      alt,
    }
  })
})
</script>
